"use client";

// src/contexts/ThemeContext.tsx
// Themes: midnight | ember | classic
import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { safeLocalStorage } from '../utils/storage';

const THEMES = ['classic', 'midnight', 'ember'] as const;
export type ThemeType = typeof THEMES[number];

function resolveTheme(saved: string | null): ThemeType {
    if (saved === 'dark' || saved === 'rainbow') return 'midnight';
    if (saved === 'pastel') return 'classic';
    if (THEMES.includes(saved as ThemeType)) return saved as ThemeType;
    return 'midnight';
}

function persistTheme(theme: ThemeType) {
    safeLocalStorage.setItem('theme', theme);
}

function applyThemeToDocument(theme: ThemeType) {
    const root = document.documentElement;
    root.classList.remove(...THEMES, 'dark', 'rainbow');
    root.classList.add(theme);
    root.style.colorScheme = (theme === 'midnight' || theme === 'ember') ? 'dark' : 'light';
}

interface ThemeContextType {
    theme: ThemeType;
    setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType>({
    theme: 'midnight',
    setTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const [theme, setThemeState] = useState<ThemeType>(() => {
        const saved = safeLocalStorage.getItem('theme');
        const initial = resolveTheme(saved);
        if (saved !== initial) persistTheme(initial);
        applyThemeToDocument(initial);
        return initial;
    });

    useEffect(() => {
        applyThemeToDocument(theme);
        persistTheme(theme);
    }, [theme]);

    const setTheme = (t: ThemeType) => setThemeState(t);

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
    return ctx;
};

// ─── Theme Selector ────────────────────────────────────────────────────────────
const THEME_META: { value: ThemeType; label: string; icon: string; description: string; preview: string }[] = [
    {
        value: 'classic',
        label: 'Light',
        icon: '☀️',
        description: 'White background with high-contrast text',
        preview: 'bg-white border-gray-300',
    },
    {
        value: 'midnight',
        label: 'Midnight',
        icon: '🌑',
        description: 'Near-black with electric blue contrast',
        preview: 'bg-[#070b14] border-[#1e4d8c]',
    },
    {
        value: 'ember',
        label: 'Ember',
        icon: '🔥',
        description: 'Deep black with warm amber glow',
        preview: 'bg-[#0c0804] border-[#92400e]',
    },
];

export const ThemeSelector: React.FC = () => {
    const { theme, setTheme } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const current = THEME_META.find(t => t.value === theme);
    const isDark = theme === 'midnight' || theme === 'ember';

    const updateMenuPosition = () => {
        const button = buttonRef.current;
        if (!button) return;

        const rect = button.getBoundingClientRect();
        const menuWidth = Math.min(360, window.innerWidth - 16);
        const left = Math.min(
            Math.max(8, rect.right - menuWidth),
            window.innerWidth - menuWidth - 8
        );

        setMenuStyle({
            top: rect.bottom + 8,
            left,
            width: menuWidth,
        });
    };

    useLayoutEffect(() => {
        if (!isOpen) {
            setMenuStyle(null);
            return;
        }

        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);

        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [isOpen]);

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(o => !o)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{
                    background: isDark ? 'rgba(255,255,255,0.07)' : 'var(--bg-surface)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border)'}`,
                    color: 'var(--text)',
                }}
                aria-label="Select theme"
                aria-expanded={isOpen}
            >
                <span className="text-base">{current?.icon}</span>
                <span className="text-sm font-medium hidden sm:inline" style={{ color: 'var(--text)' }}>
                    {current?.label}
                </span>
                <svg className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                     style={{ color: 'var(--text-muted)' }}
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
            </button>

            {isOpen && menuStyle && createPortal(
                <>
                    <div className="fixed inset-0 z-[100]" onClick={() => setIsOpen(false)}/>
                    <div
                        className="fixed rounded-xl shadow-2xl z-[110] overflow-hidden"
                        style={{
                            top: menuStyle.top,
                            left: menuStyle.left,
                            width: menuStyle.width,
                            background: isDark ? '#0e1829' : 'var(--bg-card)',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border)'}`,
                        }}
                    >

                        <div className="px-4 py-3 border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'var(--border)' }}>
                            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Theme</p>
                        </div>

                        {THEME_META.map(t => (
                            <button
                                key={t.value}
                                onClick={() => { setTheme(t.value); setIsOpen(false); }}
                                className="w-full text-left px-4 py-3 flex items-center gap-3 transition-colors"
                                style={{
                                    background: theme === t.value
                                        ? (isDark ? 'rgba(59,130,246,0.12)' : 'color-mix(in srgb, var(--accent) 12%, transparent)')
                                        : 'transparent',
                                }}
                                onMouseEnter={e => { if (theme !== t.value) (e.currentTarget as HTMLElement).style.background = isDark ? 'rgba(255,255,255,0.05)' : 'var(--bg-surface)'; }}
                                onMouseLeave={e => { if (theme !== t.value) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                            >
                                <div className={`w-8 h-8 rounded-md flex-shrink-0 border-2 ${t.preview}`}/>

                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium text-sm" style={{ color: 'var(--text)' }}>{t.icon} {t.label}</span>
                                        {theme === t.value && (
                                            <svg className="w-4 h-4" style={{ color: 'var(--accent)' }} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                            </svg>
                                        )}
                                    </div>
                                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{t.description}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

export const ThemeToggle: React.FC = () => {
    const { theme, setTheme } = useTheme();
    const cycle = () => {
        const idx = THEMES.indexOf(theme);
        setTheme(THEMES[(idx + 1) % THEMES.length]);
    };
    const icon = { midnight: '🌑', ember: '🔥', classic: '☀️' }[theme];
    return (
        <button onClick={cycle}
                className="p-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                title={`Theme: ${theme}`}>
            <span className="text-lg">{icon}</span>
        </button>
    );
};
